import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import OtherCoachActivityRow from "./otherCoachActivityRow";
import { API_BASE } from "../../config/apiConfig";
import { FaTimes, FaSearch } from "react-icons/fa";
import {
  FaArrowDownShortWide,
  FaArrowUpShortWide,
} from "react-icons/fa6";

export default function OtherCoachesActivitiesList() {
  const { id } = useParams();
  const token = sessionStorage.getItem("access_token");

  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searching, setSearching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [rankingFilter, setRankingFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("latest");

  useEffect(() => {
    if (!searching) loadPage(1);
  }, [statusFilter, typeFilter, rankingFilter, sortOrder]);

  useEffect(() => {
    if (!searching) loadPage(page);
  }, [page]);

  async function loadPage(pg) {
    setLoading(true);
    try {
      const url = new URL(
        `${API_BASE}/api/other-coaches-activities-list/${id}`
      );
      url.searchParams.append("page", pg);
      url.searchParams.append("limit", 8);
      url.searchParams.append("status", statusFilter);
      url.searchParams.append("type", typeFilter);
      url.searchParams.append("rankingType", rankingFilter);
      url.searchParams.append("sort", sortOrder);

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (data.success) {
        setActivities(data.activities);
        setTotalPages(Math.ceil(data.total / 8));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    const q = searchText.trim();
    if (q.length < 3) return;

    setSearching(true);
    setLoading(true);

    try {
      const url = new URL(
        `${API_BASE}/api/other-coaches-activities-search`
      );
      url.searchParams.append("query", q);
      url.searchParams.append("coachId", id);

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) setSearchResults(data.results);
    } finally {
      setLoading(false);
    }
  }

  function clearSearch() {
    setSearching(false);
    setSearchResults([]);
    setSearchText("");
    loadPage(1);
  }

  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />

      <PageHeader
        title="Coach's Activities"
        backTo="/other-coaches-list"
      />

      <div className="space-y-6">

        {/* FILTER ROW */}
        <div className={`flex flex-wrap items-center gap-3 ${searchText.length > 0 ? "opacity-40 pointer-events-none" : ""}`}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="All">All Status</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="draft">Draft</option>
            <option value="full">Full</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="All">All Types</option>
            <option value="event">Event</option>
            <option value="tournament">Tournament</option>
            <option value="camp">Camp</option>
            <option value="seminar">Seminar</option>
            <option value="workshop">Workshop</option>
            <option value="talk">Talk</option>
            <option value="sparring">Sparring</option>
            <option value="fitness">Fitness</option>
            <option value="friendlyMatch">Friendly Match</option>
          </select>

          {typeFilter === "tournament" && (
            <select
              value={rankingFilter}
              onChange={(e) => setRankingFilter(e.target.value)}
              className="border p-2 rounded"
            >
              <option value="All">All Rankings</option>
              <option value="National">National</option>
              <option value="AJSS">AJSS</option>
            </select>
          )}

          <button
            onClick={() =>
              setSortOrder(sortOrder === "latest" ? "oldest" : "latest")
            }
            className="px-3 py-2 bg-gray-200 rounded"
          >
            {sortOrder === "latest" ? (
              <FaArrowDownShortWide size={18} />
            ) : (
              <FaArrowUpShortWide size={18} />
            )}
          </button>
        </div>

        {/* SEARCH BAR */}
        <div className="flex items-center gap-2 mt-2 mb-4">
          <div className="relative flex-grow">
            <input
              className="w-full border rounded-lg p-3 pr-10 placeholder:italic placeholder:text-gray-400"
              placeholder="Search activity name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            {searchText.trim().length > 0 && (
              <button
                onClick={clearSearch}
                className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-500"
              >
                <FaTimes />
              </button>
            )}
          </div>

          <button
            disabled={searchText.trim().length < 3}
            onClick={handleSearch}
            className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <FaSearch />
          </button>
        </div>

        {/* LIST */}
        <div className="bg-white shadow rounded-lg divide-y">
          {(searching ? searchResults : activities).map((a, idx) => (
            <OtherCoachActivityRow key={a.id} activity={a} coachId={id} idx={idx} />
          ))}

          {!searching && activities.length === 0 && (
            <p className="text-center py-4 text-gray-500">
              No activities found.
            </p>
          )}
        </div>

        {/* PAGINATION */}
        {!searching && totalPages > 1 && (
          <div className="flex justify-center items-center gap-6 mt-4">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="disabled:opacity-40"
            >
              Prev
            </button>

            <span>
              Page {page} / {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </MainContentLayout>
  );
}
